// ─── Shared Donation Panel — used by Admin, Reception, Cashier, Restaurant Manager ─
import React, { useState, useEffect } from 'react';
import { StatCards, Card, Field, inputStyle, btnPrimary, Empty } from './UI';
import * as api from '../api/client';

export default function DonationPanel({ notify }) {
  const [donations, setDonations] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    donor_name:'', donor_email:'', category:'general', amount_kes:'',
    currency:'KES', amount_original:'', exchange_rate:1, payment_mode:'cash',
    transaction_ref:'', notes:'',
  });

  const load = async () => {
    try {
      const [d, c] = await Promise.all([api.getDonations(), api.getCurrencyRates()]);
      setDonations(d.data.results || d.data);
      setCurrencies(c.data.results || c.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const totalKes = donations.reduce((s, d) => s + parseFloat(d.amount_kes || 0), 0);

  const CATEGORIES = [
    { id:'wildlife',      label:'🦁 Wildlife Conservation' },
    { id:'reforestation', label:'🌳 Reforestation' },
    { id:'community',     label:'🤝 Community Programme' },
    { id:'ocean',         label:'🌊 Ocean / Water Conservation' },
    { id:'general',       label:'🌿 General Conservation' },
    { id:'other',         label:'📋 Other' },
  ];

  const save = async () => {
    if (!form.donor_name || !form.amount_kes) { notify('Donor name and amount required', 'error'); return; }
    try {
      await api.createDonation({
        ...form,
        amount_kes: parseFloat(form.amount_kes),
        amount_original: parseFloat(form.amount_original || form.amount_kes),
        exchange_rate: parseFloat(form.exchange_rate || 1),
      });
      notify('✅ Donation recorded!');
      setShowForm(false);
      setForm({ donor_name:'', donor_email:'', category:'general', amount_kes:'', currency:'KES',
        amount_original:'', exchange_rate:1, payment_mode:'cash', transaction_ref:'', notes:'' });
      load();
    } catch { notify('Failed to record donation', 'error'); }
  };

  const handleCurrencyChange = (code) => {
    const rate = currencies.find(c => c.code === code);
    setForm(f => ({ ...f, currency: code, exchange_rate: rate ? parseFloat(rate.rate_to_kes) : 1 }));
  };

  const handleOriginalAmountChange = (val) => {
    const kes = parseFloat(val || 0) * parseFloat(form.exchange_rate || 1);
    setForm(f => ({ ...f, amount_original: val, amount_kes: kes.toFixed(2) }));
  };

  return (
    <div>
      <StatCards cards={[
        { label:'Total Donations (KES)', value: totalKes.toLocaleString('en-KE', {minimumFractionDigits:2}), icon:'🌿', color:'#2ecc71' },
        { label:'Total Records', value: donations.length, icon:'📋', color:'#c8a96e' },
      ]} />

      <button style={{ ...btnPrimary, marginBottom:16, width:'auto' }} onClick={() => setShowForm(!showForm)}>
        {showForm ? '✕ Cancel' : '+ Record Donation'}
      </button>

      {showForm && (
        <Card title="Record New Donation" style={{ marginBottom:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
            <Field label="Donor Name *"><input style={inputStyle} value={form.donor_name} onChange={e=>setForm(f=>({...f,donor_name:e.target.value}))} placeholder="John Doe" /></Field>
            <Field label="Email"><input style={inputStyle} value={form.donor_email} onChange={e=>setForm(f=>({...f,donor_email:e.target.value}))} placeholder="donor@email.com" /></Field>
            <Field label="Category">
              <select style={inputStyle} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Currency">
              <select style={inputStyle} value={form.currency} onChange={e=>handleCurrencyChange(e.target.value)}>
                <option value="KES">KES — Kenyan Shilling</option>
                {currencies.filter(c=>c.is_active).map(c=>(
                  <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </select>
            </Field>
            {form.currency !== 'KES' ? (
              <>
                <Field label={`Amount (${form.currency})`}>
                  <input style={inputStyle} type="number" value={form.amount_original}
                    onChange={e=>handleOriginalAmountChange(e.target.value)} placeholder="100.00" />
                </Field>
                <Field label={`Rate (1 ${form.currency} = ? KES)`}>
                  <input style={inputStyle} type="number" value={form.exchange_rate}
                    onChange={e=>setForm(f=>({...f,exchange_rate:e.target.value}))} />
                </Field>
                <Field label="Equivalent KES">
                  <input style={{ ...inputStyle, background:'#1a1a1a' }} readOnly value={form.amount_kes} />
                </Field>
              </>
            ) : (
              <Field label="Amount (KES) *">
                <input style={inputStyle} type="number" value={form.amount_kes}
                  onChange={e=>setForm(f=>({...f,amount_kes:e.target.value,amount_original:e.target.value}))} placeholder="5000" />
              </Field>
            )}
            <Field label="Payment Mode">
              <select style={inputStyle} value={form.payment_mode} onChange={e=>setForm(f=>({...f,payment_mode:e.target.value}))}>
                {['cash','mpesa','bank','card','cheque'].map(m=><option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Transaction Ref">
              <input style={inputStyle} value={form.transaction_ref} onChange={e=>setForm(f=>({...f,transaction_ref:e.target.value}))} placeholder="M-Pesa code, receipt no." />
            </Field>
            <Field label="Notes" style={{ gridColumn:'1/-1' }}>
              <textarea style={{ ...inputStyle, minHeight:60 }} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} />
            </Field>
          </div>
          <button style={{ ...btnPrimary, marginTop:12 }} onClick={save}>✓ Record Donation</button>
        </Card>
      )}

      <Card title={`Donation Log (${donations.length})`}>
        {donations.length === 0 ? <Empty text="No donations recorded yet" /> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr style={{ background:'var(--bgInput)' }}>
                {['Ref','Donor','Category','Currency','Amount','KES Total','Method','Date','By'].map(h=>(
                  <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, fontWeight:700, color:'var(--textMuted)', borderBottom:'1px solid var(--border)' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {donations.map(d => (
                  <tr key={d.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'8px 10px', fontFamily:'monospace', color:'#2ecc71', fontSize:11 }}>{d.ref}</td>
                    <td style={{ padding:'8px 10px', fontWeight:600, color:'var(--textPrimary)' }}>{d.donor_name}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', textTransform:'capitalize', fontSize:11 }}>{d.category?.replace('_',' ')}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textSecond)' }}>{d.currency}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textSecond)' }}>
                      {d.currency !== 'KES' ? `${d.currency} ${parseFloat(d.amount_original||0).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding:'8px 10px', fontWeight:700, color:'#2ecc71' }}>KES {parseFloat(d.amount_kes||0).toLocaleString()}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', textTransform:'capitalize' }}>{d.payment_mode}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>{new Date(d.donated_at).toLocaleDateString('en-KE')}</td>
                    <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>{d.recorded_by_name}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:'var(--bgInput)', fontWeight:700 }}>
                  <td colSpan={5} style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>Total</td>
                  <td colSpan={4} style={{ padding:'8px 10px', color:'#2ecc71', fontWeight:700 }}>
                    KES {totalKes.toLocaleString('en-KE', {minimumFractionDigits:2})}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
